import { useState, useEffect, useCallback, useRef } from "react";
import { useApiError } from "./useApiError";
import { BitmakerAPIClient } from "../services/BitmakerApp";
import {
  SprintUserSummary,
  SprintUserSummaryFromJSON,
} from "../bitmaker/schemas/Sprints/SprintUserSummary";

export const useSprintUserSummary = (login?: string, sprintId?: string) => {
  const [sprintSummary, setSprintSummary] = useState<SprintUserSummary[]>([]);
  const [loadingSprintSummary, setLoadingSprintSummary] = useState(false);
  const [fetchSprintSummaryError, setFetchSprintSummaryError] = useState<
    string | null
  >(null);

  const { handleAPIError } = useApiError();
  const isMountedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchUserSprintSummary = useCallback(() => {
    if (!isMountedRef.current || !login) return;

    setLoadingSprintSummary(true);
    setFetchSprintSummaryError(null);

    const url = sprintId
      ? `/sprints/${sprintId}/users/${login}/summary`
      : `/sprints/current/users/${login}/summary`;

    BitmakerAPIClient.get(url, null, (response) => {
      if (!isMountedRef.current) return;

      const { isError, message } = handleAPIError(response);

      if (isError) {
        console.error("Error fetching user sprint summary:", message);
        setFetchSprintSummaryError(
          "Error fetching user sprint summary. Please try again.",
        );
        setLoadingSprintSummary(false);
        return;
      }

      const data = response.data;
      setSprintSummary(
        data && typeof data === "object"
          ? [SprintUserSummaryFromJSON(data)]
          : [],
      );
      setLoadingSprintSummary(false);
    });
  }, [login, sprintId, handleAPIError]);

  useEffect(() => {
    fetchUserSprintSummary();
  }, [fetchUserSprintSummary]);

  return {
    sprintSummary,
    loadingSprintSummary,
    fetchSprintSummaryError,
    refetchSprintSummary: fetchUserSprintSummary,
  };
};
